import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { MrraStatusCdEnum } from '@shared/enums/mrra-status-cd.enum';
import { CreateMassRerateRequestModel } from '@shared/models/create-mass-rerate-request.model';
import { GetMrraListRequestModel } from '@shared/models/get-mrra-list-request.model';
import { CreateAdhocMassRerateRqst } from '@xpo-ltl-2.0/sdk-rating';
import { map, take } from 'rxjs/operators';
import { MrraActions } from '../actions/mrra.actions';
import { MrraSelectors } from '../selectors/mrra.selector';
import { MrraTraySummaryModel } from '@shared/models/mrra-tray-summary.model';
import { ProgressMassRerateRequestModel } from '@shared/models/progress-mass-rerate-request.model';
import { ProgressReratePathParamsModel } from '@shared/models/progress-rerate-path-params.model';
import { WhatIfDownloadFileTypeCd } from '@xpo-ltl/sdk-common';
import { MrraStatusCountModel } from '@shared/models/mrra-list.model';
import { UpdateMassRerateRequestModel } from '@shared/models/update-mass-rerate-request.model';
import { UpdateMassReratePathParamsModel } from '@shared/models/update-mass-rerate-path-params.model';
import { MrraTrayRerateModel } from '@shared/models/mrra-tray-rerate.model';

@Injectable({
  providedIn: 'root',
})
export class MrraHandlerService {
  // ACTION ###########################################################################################

  // Create MRRA Actions ************************************************************************
  createMassRerateSuccess$ = this.actions$.pipe(
    ofType(MrraActions.createMassRerateSuccess),
    map(({ createMassRerateResp }) => createMassRerateResp)
  );

  createMassRerateFail$ = this.actions$.pipe(
    ofType(MrraActions.createMassRerateFail),
    map(({ errors }) => errors)
  );

  // List MRRA Actions ************************************************************************
  listMassRerateRequestsSuccess$ = this.actions$.pipe(
    ofType(MrraActions.listMassRerateSuccess),
    map(({ mrraList }) => mrraList)
  );

  listMassRerateRequestsFail$ = this.actions$.pipe(
    ofType(MrraActions.listMassRerateFail),
    map(({ errors }) => errors)
  );

  // Create NEW MRRA Actions ************************************************************************
  createNewMassRerateSuccess$ = this.actions$.pipe(
    ofType(MrraActions.createNewMassRerateRequestSuccess),
    map(({ createdNewMassRerate }) => createdNewMassRerate)
  );

  createNewMassRerateFail$ = this.actions$.pipe(
    ofType(MrraActions.createNewMassRerateRequestFail),
    map(({ errors }) => errors)
  );

  // Progress NEW MRRA Actions ************************************************************************
  progressMassRerateSuccess$ = this.actions$.pipe(
    ofType(MrraActions.progressMassRerateRequestSuccess),
    map(({ progressMassRerateResp }) => progressMassRerateResp)
  );

  progressMassRerateFail$ = this.actions$.pipe(
    ofType(MrraActions.progressMassRerateRequestFail),
    map(({ errors }) => errors)
  );

  // Update MRRA Actions ************************************************************************
  updateMassRerateSuccess$ = this.actions$.pipe(
    ofType(MrraActions.updateRerateRequestSuccess),
    map(({ updatedMassRerate }) => updatedMassRerate)
  );

  updateMassRerateFail$ = this.actions$.pipe(
    ofType(MrraActions.updateRerateRequestFail),
    map(({ errors }) => errors)
  );

  // Download MRRA Actions ******************************************************************************************
  downloadShipmentFileSuccess$ = this.actions$.pipe(
    ofType(MrraActions.mrraRequestsDownloadShipmentFileSuccess)
  );
  
  downloadShipmentFileFail$ = this.actions$.pipe(
    ofType(MrraActions.mrraRequestsDownloadShipmentFileFail),
    map((action) => action.errors)
  );

  // Get filtered accounts actions *************************************************************************************************
  accountsFilteredSuccessAction$ = this.actions$.pipe(
    ofType(MrraActions.mrraGetAccountsFilteredSuccess),
    map((action) => action.accounts)
  );
  
  accountsFilteredErrorAction$ = this.actions$.pipe(
    ofType(MrraActions.mrraGetAccountsFilteredFail),
    map((action) => action.errors)
  );
  
  accountsFilteredOverrideSuccessAction$ = this.actions$.pipe(
    ofType(MrraActions.mrraGetAccountsOverrideFilteredSuccess),
    map((action) => action.accounts)
  );
  
  accountsFilteredOverrideErrorAction$ = this.actions$.pipe(
    ofType(MrraActions.mrraGetAccountsOverrideFilteredFail),
    map((action) => action.errors)
  );

  // SELECTORS ************************************************************************
  getGridFilterSelector$ = this.store$.pipe(select(MrraSelectors.getGridFilter));

  getWhatIfRequestDataSelector$ = this.store$.pipe(select(MrraSelectors.getWhatIfRequestData));

  getStatusCountsSelector$ = this.store$.pipe(select(MrraSelectors.getStatusCounts));

  requestGetAccountLoading$ = this.store$.pipe(select(MrraSelectors.selectMrraRequestGetAccountLoading));

  requestGetAccountOverrideLoading$ = this.store$.pipe(
    select(MrraSelectors.selectMrraRequestGetAccountOverrideLoading)
  );

  constructor(private store$: Store, private actions$: Actions) { }

  clearMrraStore(): void {
    this.store$.dispatch(MrraActions.clearMrraStore());
  }

  createMassRerate(request: CreateAdhocMassRerateRqst): void {
    this.store$.dispatch(MrraActions.createMassRerate({ request }))
  }

  listMassRerateRequests(request: GetMrraListRequestModel): void {
    this.store$.dispatch(MrraActions.listMassRerate(({ request })))
  }

  createNewMassRerate(createMassRerateRequest: CreateMassRerateRequestModel): void {
    this.store$.dispatch(MrraActions.createNewMassRerateRequest({ createMassRerateRequest }))
  }

  progressMassRerate(progressMassRerateRequest: ProgressMassRerateRequestModel, progressReratePathParams: ProgressReratePathParamsModel): void {
    this.store$.dispatch(MrraActions.progressMassRerateRequest({ progressMassRerateRequest, progressReratePathParams }))
  }

  updateMassRerate(updateMassRerateRequest: UpdateMassRerateRequestModel, updateMassReratePathParams: UpdateMassReratePathParamsModel): void {
    this.store$.dispatch(MrraActions.updateMassRerateRequest({ updateMassRerateRequest, updateMassReratePathParams }))
  }

  setGridStatusCdFilters(statusCdFilters: MrraStatusCdEnum[]): void {
    let storedFilters = {};
    this.getGridFilterSelector$.pipe(take(1)).subscribe(
      filts => {
        if (!filts) return;
        storedFilters = filts;
      }
    );

    const filters = {
      ...storedFilters,
      statusCds: statusCdFilters
    };

    this.store$.dispatch(MrraActions.setGridFilters({ filters }));
  }

  setWhatIfRequestActions(whatIfRequestData: MrraTraySummaryModel): void {
    this.store$.dispatch(MrraActions.setWhatIfRequestActions({ whatIfRequestData }))
  }

  downloadShipmentFile(
    fileId: string,
    whatIfDownloadFileTypeCd: WhatIfDownloadFileTypeCd,
  ): void {
    this.store$.dispatch(
      MrraActions.mrraRequestsDownloadShipmentFile({ fileId, whatIfDownloadFileTypeCd })
    );
  }

  setStatusCountsActions(mrraStatusCounts: MrraStatusCountModel[]): void {
    this.store$.dispatch(MrraActions.setStatusCountsActions({ mrraStatusCounts }));
  }

  getAccountsFiltered(filterData: string): void {
    this.store$.dispatch(MrraActions.mrraGetAccountsFiltered({ filterData }));
  }
  
  getAccountsOverrideFiltered(filterData: string): void {
    this.store$.dispatch(MrraActions.mrraGetAccountsOverrideFiltered({ filterData }));
  }
}
